import { Injectable } from '@angular/core';
import { isFuture } from 'date-fns';
import { ISession } from '~app/shared/services/auth/Session.interface';
import { environment } from '~env';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  public authToken: string;
  private sessionData: ISession;

  public authenticate(fragment?: string): boolean {
    try {
      // If there is a token on the url, replace current session
      if (fragment) this.login(fragment);

      // If there was a previous session open, or the user logged in using a fragment, validate the session
      if (this.isCurrentSessionValid) return true;

      // If none of the above worked, try to recover the session from local storage
      this.recoverSessionFromStorage();
      if (this.isCurrentSessionValid) return true;
      window.localStorage.removeItem('authToken');

      // If nothing worked, ask the user for credentials
      this.redirectToLogin();
      return false;
    } catch (error) {
      console.error(error);
      throw error;
    }
  }

  public get crewMemberId(): string {
    if (!this.sessionData) return;
    return this.sessionData.payload.sessionid.pre_check.data[0].user;
  }

  public get sessionId(): string {
    if (!this.sessionData) return;
    return this.sessionData.payload.sessionid.pre_check.data[0].sid;
  }

  public get isManager(): boolean {
    if (!this.sessionData) return false;
    for (const role of environment.auth.managerGroup) {
      if (this.roles.includes(role)) return true;
    }
    return false;
  }

  public get isHRManager(): boolean {
    if (!this.sessionData) return false;
    if (this.role.includes('HR Manager') || this.role.includes(environment.auth.adminGroup)) return true;
    return false;
  }

  public get isAdmin(): boolean {
    if (!this.sessionData) return false;
    if (this.role.includes(environment.auth.adminGroup)) return true;
    return false;
  }

  public get shipCode(): string {
    if (!this.isManager) return;
    return this.sessionData.payload.Ship.Code;
  }

  public get role(): string {
    const role = this.roles?.[0] ?? false;
    if (role) return role;
  }

  public redirectToLogin(): void {
    window.location.replace(environment.auth.authPage);
  }

  private recoverSessionFromStorage(): void {
    const tokenFromStorage = window.localStorage.getItem('authToken');
    if (tokenFromStorage) this.login(tokenFromStorage);
  }

  private login(token: string): void {
    this.authToken = undefined;
    this.sessionData = undefined;
    const sessionData = this.extractSessionFromJWT(token);
    const isValid = this.isSessionValid(sessionData);
    if (isValid) {
      this.authToken = token;
      this.sessionData = sessionData;
      window.localStorage.setItem('authToken', token);
    }
  }

  private extractSessionFromJWT(token: string): ISession {
    const session: ISession = JSON.parse(atob(token.split('.')[1]));
    // tslint:disable-next-line:no-console
    console.log('[AUTH] Token', session);
    if (!session.exp) throw new Error('Missing session expiration');
    if (!session.payload.sessionid.pre_check.data[0].user) throw new Error('Missing session crew member id');
    if (!session.payload.sessionid.pre_check.data[0].sid) throw new Error('Missing session id');
    if (!session.payload.Roles) throw new Error('Missing session roles');
    for (const role of environment.auth.managerGroup) {
      if (session.payload.Roles.includes(environment.auth.adminGroup)) {
        session.payload.Ship = {
          Code: 'FR',
        };
      }
      if (session.payload.Roles.includes(role)) {
        if (!session.payload.Ship) throw new Error('Missing session ship');
        if (!session.payload.Ship.Code) throw new Error('Missing session ship code');
        break;
      }
    }
    return session;
  }

  private isSessionValid(session: ISession): boolean {
    const expiration = session.exp * 1000;
    return isFuture(expiration);
  }

  private get isCurrentSessionValid(): boolean {
    if (!this.authToken) return false;
    if (!this.sessionData) return false;
    if (!this.crewMemberId) return false;
    if (!this.isSessionValid(this.sessionData)) return false;
    return true;
  }

  private get roles(): Array<string> {
    if (!this.sessionData) {
      return [];
    }
    return this.sessionData?.payload.Roles;
  }
}
